const API_BASE = '/api/v1'

export class ApiError extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly status: number,
    readonly details?: unknown,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

function readCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`))
  return match?.[1] ? decodeURIComponent(match[1]) : null
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE'
  body?: unknown
  query?: Record<string, string | number | boolean | undefined>
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const url = new URL(API_BASE + path, window.location.origin)
  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value !== undefined) url.searchParams.set(key, String(value))
    }
  }
  return url.pathname + url.search
}

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const method = options.method ?? 'GET'
  const headers: Record<string, string> = {}

  if (options.body !== undefined) headers['Content-Type'] = 'application/json'
  if (method !== 'GET') {
    const csrfToken = readCookie('ntl_csrf')
    if (csrfToken) headers['X-CSRF-Token'] = csrfToken
  }

  const response = await fetch(buildUrl(path, options.query), {
    method,
    headers,
    credentials: 'include',
    body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
  })

  const isJson = response.headers.get('content-type')?.includes('application/json')
  const payload = isJson ? await response.json().catch(() => null) : null

  if (!response.ok) {
    const errorBody = payload?.error as { code?: string; message?: string; details?: unknown } | undefined
    throw new ApiError(
      errorBody?.code ?? 'UNKNOWN_ERROR',
      errorBody?.message ?? `Request fehlgeschlagen (${response.status})`,
      response.status,
      errorBody?.details,
    )
  }

  return payload as T
}

// Für multipart/form-data-Uploads (z.B. Avatar) — bewusst getrennt von apiRequest, da der
// Content-Type-Header dort dem Browser überlassen werden muss (er setzt die Boundary
// selbst); ein manuell gesetzter 'multipart/form-data'-Header ohne Boundary würde das
// Parsing serverseitig brechen.
export async function apiUpload<T>(path: string, file: File): Promise<T> {
  const headers: Record<string, string> = {}
  const csrfToken = readCookie('ntl_csrf')
  if (csrfToken) headers['X-CSRF-Token'] = csrfToken

  const formData = new FormData()
  formData.append('file', file)

  const response = await fetch(buildUrl(path), {
    method: 'POST',
    headers,
    credentials: 'include',
    body: formData,
  })

  const isJson = response.headers.get('content-type')?.includes('application/json')
  const payload = isJson ? await response.json().catch(() => null) : null

  if (!response.ok) {
    const errorBody = payload?.error as { code?: string; message?: string; details?: unknown } | undefined
    throw new ApiError(
      errorBody?.code ?? 'UNKNOWN_ERROR',
      errorBody?.message ?? `Request fehlgeschlagen (${response.status})`,
      response.status,
      errorBody?.details,
    )
  }

  return payload as T
}
